// src/pages/Dashboard.js — simplified: removed Invoice Pipeline chart,
// Team Status sidebar, and Quick Actions (kept just KPIs + the invoice
// table, now full width). "Pending Vouchers" KPI replaced with "Ready to
// Deliver" (stage_4_packing count), positioned right after "In Transit".
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get } from '../utils/api';
import { KpiCard, StatusBadge, Spinner, formatDate, formatCurrency } from '../components/Shared';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

// toISOString() is always UTC — adjust by the browser's own offset first so
// "Today" always means the actual local calendar date.
function localDateString(d) {
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().split('T')[0];
}
const today = () => localDateString(new Date());

const PAGE_SIZE = 20;
const STAGES = [
  { value: '', label: 'All Stages' },
  { value: 'stage_1_erp',           label: 'ERP Received' },
  { value: 'stage_2_stock_removal', label: 'Stock Removal' },
  { value: 'stage_3_checking',      label: 'Checking' },
  { value: 'stage_4_packing',       label: 'Ready for Delivery' },
  { value: 'stage_5_dispatched',    label: 'In Transit' },
  { value: 'stage_6_delivered',     label: 'Delivered' },
  { value: 'reattempt_pending',     label: 'Reattempt' },
  { value: 'cancelled',             label: 'Cancelled' },
  { value: 'complete',              label: 'Complete' },
];

export default function Dashboard() {
  const [kpi,          setKpi]          = useState(null);
  const [readyToDeliver, setReadyToDeliver] = useState(null);
  const [invoices,     setInvoices]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [filter,       setFilter]       = useState('');
  const [statusFilter, setStatus]       = useState('');
  const [dateFrom,     setDateFrom]     = useState(today());
  const [dateTo,       setDateTo]       = useState(today());
  const [sortCol,      setSortCol]      = useState('mng_number');
  const [sortOrder,    setSortOrder]    = useState('desc');
  const [page,         setPage]         = useState(1);
  const [total,        setTotal]        = useState(0);
  const [loadingInv,   setLoadingInv]   = useState(false);
  const navigate = useNavigate();

  // Load KPI + "Ready to Deliver" count once
  const loadMeta = useCallback(async () => {
    try {
      const [kpiData, readyData] = await Promise.all([
        get('/api/invoices/kpi/today'),
        get(`/api/invoices?status=stage_4_packing&limit=1&offset=0&date_from=${today()}&date_to=${today()}`),
      ]);
      setKpi(kpiData);
      setReadyToDeliver(readyData.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  // Load invoices separately — supports filter + pagination + date range + sorting
  const loadInvoices = useCallback(async (pg = 1, status = '', search = '', from = '', to = '', col = 'mng_number', ord = 'desc') => {
    setLoadingInv(true);
    try {
      const offset = (pg - 1) * PAGE_SIZE;
      let url = `/api/invoices?limit=${PAGE_SIZE}&offset=${offset}&sort=${col}&order=${ord}`;
      if (status) url += `&status=${encodeURIComponent(status)}`;
      // Use invoice_number param — backend now searches both invoice_number AND party_name
      if (search) url += `&invoice_number=${encodeURIComponent(search)}`;
      if (from)   url += `&date_from=${encodeURIComponent(from)}`;
      if (to)     url += `&date_to=${encodeURIComponent(to)}`;
      const data = await get(url);
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoadingInv(false); }
  }, []);

  const lastUpdated = useAutoRefresh(loadMeta);
  useAutoRefresh(() => loadInvoices(page, statusFilter, filter, dateFrom, dateTo, sortCol, sortOrder));

  useEffect(() => {
    loadInvoices(page, statusFilter, filter, dateFrom, dateTo, sortCol, sortOrder);
  }, [page, statusFilter, loadInvoices]); // eslint-disable-line

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      loadInvoices(1, statusFilter, filter, dateFrom, dateTo, sortCol, sortOrder);
    }, 400);
    return () => clearTimeout(t);
  }, [filter]); // eslint-disable-line

  // ADDED: Listen for Settings changes via custom event
  // When user updates invoice flow or overrides in Settings page, this refreshes the data
  useEffect(() => {
    const handleSettingsChange = () => {
      console.log('Settings updated, refreshing Dashboard data...');
      loadMeta();
      loadInvoices(page, statusFilter, filter, dateFrom, dateTo, sortCol, sortOrder);
    };

    // Listen for storage changes (works across tabs)
    window.addEventListener('storage', (e) => {
      if (e.key === 'dms-settings-changed') {
        handleSettingsChange();
      }
    });

    // Also listen for local events (same tab)
    window.addEventListener('dms-settings-changed', handleSettingsChange);
    return () => {
      window.removeEventListener('storage', handleSettingsChange);
      window.removeEventListener('dms-settings-changed', handleSettingsChange);
    };
  }, [page, statusFilter, filter, dateFrom, dateTo, sortCol, sortOrder, loadMeta, loadInvoices]);

  function handleStatusChange(val) {
    setStatus(val);
    setPage(1);
    loadInvoices(1, val, filter, dateFrom, dateTo, sortCol, sortOrder);
  }

  function handleDateChange(from, to) {
    setPage(1);
    loadInvoices(1, statusFilter, filter, from, to, sortCol, sortOrder);
  }

  function clearDates() {
    setDateFrom('');
    setDateTo('');
    setPage(1);
    loadInvoices(1, statusFilter, filter, '', '', sortCol, sortOrder);
  }

  function handleSort(col) {
    let newOrder;
    if (col === sortCol) {
      newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      // invoice_number → desc first. party_name → asc first.
      newOrder = col === 'party_name' ? 'asc' : 'desc';
    }
    setSortCol(col);
    setSortOrder(newOrder);
    setPage(1);
    loadInvoices(1, statusFilter, filter, dateFrom, dateTo, col, newOrder);
  }

  function sortIcon(col) {
    if (sortCol !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-600 ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
          </p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-0.5">
              🔄 Auto-refreshing · Last updated {lastUpdated.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
            </p>
          )}
        </div>
        <button onClick={() => { loadMeta(); loadInvoices(page, statusFilter, filter, dateFrom, dateTo, sortCol, sortOrder); }}
          className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
          🔄 Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Invoices Today"   value={kpi?.invoices_today}    color="blue"   icon="📋" />
        <KpiCard label="Ready to Deliver" value={readyToDeliver}         color="amber"  icon="📦" />
        <KpiCard label="In Transit"       value={kpi?.in_transit}        color="purple" icon="🚚"
          sub={`${kpi?.reattempt_pending || 0} reattempts pending`} />
        <KpiCard label="Completed Today"  value={kpi?.completed_today}   color="green"  icon="✅"
          sub={`${kpi?.cancelled_today || 0} cancelled`} />
      </div>

      {/* Invoices table — now full width, pipeline chart and team
          sidebar removed */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-4 border-b bg-gray-50 space-y-3">
          <div className="flex gap-3 items-center flex-wrap">
            <input
              type="text"
              placeholder="Search invoice # or party..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STAGES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">From</label>
              <input type="date" value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); handleDateChange(e.target.value, dateTo); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">To</label>
              <input type="date" value={dateTo}
                onChange={e => { setDateTo(e.target.value); handleDateChange(dateFrom, e.target.value); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button onClick={clearDates} className="text-xs text-gray-400 hover:text-red-500" title="Clear dates">✕</button>
            )}
          </div>
          <span className="text-sm text-gray-500 whitespace-nowrap">
            {total} invoice{total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto relative">
          {loadingInv && (
            <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center z-10">
              <Spinner />
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th onClick={() => handleSort('mng_number')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  Invoice #{sortIcon('mng_number')}
                </th>
                <th onClick={() => handleSort('party_name')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  Party{sortIcon('party_name')}
                </th>
                <th onClick={() => handleSort('status')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  Stage{sortIcon('status')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Handler</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Time at Stage</th>
                <th onClick={() => handleSort('value')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none">
                  Value{sortIcon('value')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">No invoices found</td></tr>
              ) : invoices.map(inv => {
                const mins = inv.seconds_in_current_stage ? Math.floor(inv.seconds_in_current_stage / 60) : null;
                return (
                  <tr key={inv.invoice_id} onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                    className="border-t hover:bg-blue-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-semibold text-blue-900">{inv.invoice_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{inv.party_name}</div>
                      <div className="text-xs text-gray-400">{inv.city}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{inv.current_handler_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {mins !== null ? (mins < 60 ? `${mins}m` : `${Math.floor(mins/60)}h ${mins%60}m`) : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium">{formatCurrency(inv.invoice_value)}</td>
                    <td className="px-4 py-3"><span className="text-blue-600 text-xs hover:underline">View →</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Page {page} of {totalPages} · Showing {((page-1)*PAGE_SIZE)+1}–{Math.min(page*PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2 py-1 text-xs border rounded disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => setPage(p => p-1)} disabled={page === 1}
                className="px-3 py-1 text-xs border rounded disabled:opacity-40 hover:bg-gray-50">‹ Prev</button>
              {/* Dynamic page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx-1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '...' ? (
                  <span key={`e${i}`} className="px-2 py-1 text-xs text-gray-400">…</span>
                ) : (
                  <button key={p} onClick={() => setPage(p)}
                    className={`px-3 py-1 text-xs border rounded ${page === p ? 'bg-blue-900 text-white border-blue-900' : 'hover:bg-gray-50'}`}>
                    {p}
                  </button>
                ))
              }
              <button onClick={() => setPage(p => p+1)} disabled={page === totalPages}
                className="px-3 py-1 text-xs border rounded disabled:opacity-40 hover:bg-gray-50">Next ›</button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2 py-1 text-xs border rounded disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
